/**
 * Note Metadata Component
 *
 * Displays patient assignment and encounter information below the NoteTopBar.
 *
 * Features:
 * - 80px height with #FCFCFC background
 * - Patient assignment or patient name display with pronouns
 * - Encounter date, start time, and duration from encounter data (with fallback to transcription data)
 * - Editable encounter time when data comes from encounter (not transcription)
 * - Horizontal layout with dividers between items
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AssignNoteToPatient from '@components/AssignNoteToPatient';
import { MODAL_TYPES } from '@components/modals/constants';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { cleanCptCodes, CPTCodePicker } from '@components/ui/forms/CPTCodePicker';
import { ICD10CodePicker } from '@components/ui/forms/ICD10CodePicker/ICD10CodePicker';
import { EligibilityBadge, EligibilityWarningIcon } from '@features/eligibility-checks';
import { useDebouncedWorkingBillingClaim } from '@features/encounters';
import {
  bareCodesToLineItems,
  buildDiagnosisCodeSources,
  extractBillingCodes,
  extractDiagnosisCodes,
  hasDiagnosisCodes,
  hasLineItems,
} from '@features/encounters/utils/billingClaim';
import { notesApi } from '@features/notes';
import {
  PatientAllergiesPopover,
  PatientDiagnosesEditModal,
  PatientDiagnosesPopover,
  PatientIndicators,
  PatientLastEncounterPopover,
  PatientMedicationsEditModal,
  PatientMedicationsPopover,
  useIndicatorHover,
  usePatientContext,
} from '@features/patient-context-bar';
import { useAccessToken, useSubjectLanguage } from '@features/user';
import { UserIcon } from '@icons/UserIcon';
import {
  AttachMoney,
  CalendarToday,
  Check as CheckIcon,
  Close as CloseIcon,
  HealthAndSafety,
  LocalHospital,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  Container,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import APIService from '@services/APIService';
import { useEventTracking, useFeatureGate } from '@services/statsig';
import type { EncounterResponse } from '@store/generated/encounters';
import type { NoteResponse } from '@store/generated/notes';
import { useAppDispatch, useModal } from '@store/hooks';
import type { Patient } from '@ts/types';
import { formatPatientName } from '@utils/patientUtils';
import { capitalize, formatDate } from '@utils/utils';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

import { LAYOUT_SPACING } from '../constants/layout';

export interface NoteMetadataProps {
  /** Note ID for patient assignment */
  noteId: string;
  /** Note data with patient and transcription information */
  noteData?: NoteResponse;
  /** Encounter data with start_time, end_time, and duration */
  encounterData?: EncounterResponse;
  /** Whether patient assignment/unassignment is allowed */
  editable?: boolean;
  /** AI-suggested CPT codes from audit analysis (shown as ghost when no codes exist) */
  suggestedCptCodes?: string[];
  /** Callback when user clicks to apply suggested CPT codes */
  onUseSuggestedCodes?: () => void;
  /** Callback when viewing last encounter note from patient context */
  onViewLastEncounterNote?: (noteId: string) => void;
  /** Callback when CPT codes are changed (for triggering reaudit) */
  onCptCodesChanged?: (codes: string[]) => void;
  /** When provided, chip clicks open the unified billing popover instead of inline editors */
  onOpenBillingPopover?: (anchorEl: HTMLElement, section?: 'session' | 'billing' | 'attestations') => void;
  /** Callback when user clicks the payer chip to open patient insurance pane */
  onPayerClick?: () => void;
}

/**
 * Metadata item display component
 * Shows icon + text with tooltip
 */
const MetadataItem: React.FC<{
  icon: React.ReactNode;
  text: string;
  tooltip: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  clickable?: boolean;
}> = ({ icon, text, tooltip, onClick, clickable = false }) => {
  const theme = useTheme();

  return (
    <Tooltip title={tooltip} arrow>
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color: theme.palette.text.secondary,
          ...(clickable && {
            cursor: 'pointer',
            borderRadius: 1,
            px: 1,
            py: 0.5,
            mx: -1,
            my: -0.5,
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }),
        }}
      >
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', width: 20, height: 20 }}>
          {icon}
        </Box>
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.875rem',
            fontWeight: 400,
            whiteSpace: 'nowrap',
          }}
        >
          {text}
        </Typography>
      </Box>
    </Tooltip>
  );
};

/**
 * Note Metadata Bar Component
 */
export const NoteMetadata: React.FC<NoteMetadataProps> = ({
  noteId,
  noteData,
  encounterData,
  editable = true,
  suggestedCptCodes,
  onUseSuggestedCodes,
  onViewLastEncounterNote,
  onCptCodesChanged,
  onOpenBillingPopover,
  onPayerClick,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { getAccessToken } = useAccessToken();
  const hasEligibilityChecks = useFeatureGate('feature_eligibility_checks');
  const { enqueueSnackbar } = useSnackbar();
  const { note } = useEventTracking();
  const { open: openModal } = useModal();

  // State for hover and modal
  const [isHovering, setIsHovering] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);

  // State for CPT codes editing
  const [cptAnchorEl, setCptAnchorEl] = useState<HTMLElement | null>(null);

  // State for ICD-10 codes editing
  const [icd10AnchorEl, setIcd10AnchorEl] = useState<HTMLElement | null>(null);

  // Check if patient is assigned
  const patient = noteData?.patient;

  // Handler to open insurance modal
  const handleAddInsurance = useCallback(() => {
    if (patient?.patient_id) {
      openModal(MODAL_TYPES.INSURANCE_EDIT, {
        patientId: patient.patient_id,
      });
    }
  }, [patient?.patient_id, openModal]);
  const hasPatient = Boolean(patient);
  const hasPatientId = Boolean(noteData?.patient_id);
  const subjectLanguage = useSubjectLanguage();
  const encounterId = noteData?.encounter?.encounter_id;

  const { update: updateBillingClaim } = useDebouncedWorkingBillingClaim({
    encounterId,
    onSuccess: (merged) => {
      // Trigger reaudit after successful CPT code update
      if (merged.lineItems && onCptCodesChanged) {
        const codes = extractBillingCodes({ line_items: merged.lineItems } as any);
        onCptCodesChanged(codes);
      }
    },
  });

  // Patient context data and hover state
  const {
    medications,
    diagnoses,
    allergies,
    lastEncounter,
    loadingState,
    medicationsModuleInfo,
    diagnosesModuleInfo,
  } = usePatientContext(patient?.patient_id || null);

  // Hover state for each indicator
  const medicationsHover = useIndicatorHover();
  const diagnosesHover = useIndicatorHover();
  const allergiesHover = useIndicatorHover();
  const lastEncounterHover = useIndicatorHover();

  // Modal state for patient context editing
  const [medicationsModalOpen, setMedicationsModalOpen] = useState(false);
  const [diagnosesModalOpen, setDiagnosesModalOpen] = useState(false);

  // Determine if time data comes from encounter (editable) or transcription (read-only)
  const hasEncounterTimeData = Boolean(
    encounterData?.start_time || encounterData?.appointment?.start_time,
  );

  /**
   * Handle opening time/session editor via unified billing popover
   */
  const handleOpenTimeEditor = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      note.encounterTimeEditorOpened({
        note_id: noteId,
        encounter_id: encounterId,
      });
      if (onOpenBillingPopover) {
        onOpenBillingPopover(event.currentTarget, 'session');
        return;
      }
    },
    [noteId, encounterId, note, onOpenBillingPopover],
  );

  /**
   * Handle opening CPT editor popover
   */
  const handleOpenCptEditor = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      note.encounterCptEditorOpened({
        note_id: noteId,
        encounter_id: encounterId,
        has_existing_codes: hasLineItems(encounterData?.working_billing_claim),
      });
      // Delegate to unified billing popover when available
      if (onOpenBillingPopover) {
        onOpenBillingPopover(event.currentTarget);
        return;
      }
      setCptAnchorEl(event.currentTarget);
    },
    [noteId, encounterId, encounterData?.working_billing_claim, note, onOpenBillingPopover],
  );

  /**
   * Handle CPT codes change - auto-save immediately
   * CPTCodePicker now only fires onChange when codes are committed (not on typing)
   * Reaudit is triggered via onSuccess callback in useDebouncedWorkingBillingClaim
   */
  const handleBillingCodesChange = useCallback(
    (codes: string[]) => {
      const cleanedCodes = cleanCptCodes(codes);
      const claim = encounterData?.working_billing_claim;

      // Track event
      note.encounterCptCodesSaved({
        note_id: noteId,
        encounter_id: encounterId,
        cpt_codes: cleanedCodes,
        cpt_codes_count: cleanedCodes.length,
      });

      updateBillingClaim({
        lineItems: bareCodesToLineItems(cleanedCodes, claim?.line_items ?? undefined),
      });
    },
    [encounterData?.working_billing_claim, noteId, encounterId, note, updateBillingClaim],
  );

  /**
   * Handle closing CPT editor popover
   */
  const handleCloseCptEditor = useCallback(() => {
    setCptAnchorEl(null);
  }, []);

  const isCptEditorOpen = Boolean(cptAnchorEl);

  /**
   * Handle ICD-10 codes change - auto-save immediately
   * ICD10CodePicker only fires onChange when codes are added/removed (not on typing)
   */
  const handleDiagnosisCodesChange = useCallback(
    (codes: string[]) => {
      const filtered = codes.filter(c => c && c.trim() !== '');
      const claim = encounterData?.working_billing_claim;

      // Track event
      note.encounterIcd10CodesSaved?.({
        note_id: noteId,
        encounter_id: encounterId,
        icd10_codes: filtered,
        icd10_codes_count: filtered.length,
      });

      updateBillingClaim({
        diagnosisCodes: filtered,
        diagnosisCodeSources: buildDiagnosisCodeSources(
          filtered,
          claim?.diagnosis_code_sources,
        ),
      });
    },
    [encounterData?.working_billing_claim, noteId, encounterId, note, updateBillingClaim],
  );

  /**
   * Handle opening ICD-10 editor popover
   */
  const handleOpenIcd10Editor = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      note.encounterIcd10EditorOpened?.({
        note_id: noteId,
        encounter_id: encounterId,
        has_existing_codes: hasDiagnosisCodes(encounterData?.working_billing_claim),
      });
      // Delegate to unified billing popover when available
      if (onOpenBillingPopover) {
        onOpenBillingPopover(event.currentTarget);
        return;
      }
      setIcd10AnchorEl(event.currentTarget);
    },
    [noteId, encounterId, encounterData?.working_billing_claim, note, onOpenBillingPopover],
  );

  /**
   * Handle closing ICD-10 editor popover
   */
  const handleCloseIcd10Editor = useCallback(() => {
    setIcd10AnchorEl(null);
  }, []);

  const isIcd10EditorOpen = Boolean(icd10AnchorEl);

  /**
   * Handle patient assignment with optimistic update
   * Updates RTK Query cache for immediate UI feedback
   */
  const handleAssignPatient = useCallback(
    (assignedPatient: Patient) => {
      dispatch(
        notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
          draft.patient = assignedPatient as any;
          draft.patient_id = assignedPatient.patient_id;
        }),
      );
    },
    [dispatch, noteId],
  );

  /**
   * Handle patient assignment error
   * Invalidates cache to refetch fresh data
   */
  const handleAssignError = useCallback(() => {
    dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
  }, [dispatch, noteId]);

  /**
   * Handle patient unassignment error
   * Invalidates cache to refetch fresh data
   */
  const handleUnassignError = useCallback(() => {
    dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
  }, [dispatch, noteId]);

  /**
   * Handle patient unassignment with optimistic update
   * Removes patient from RTK Query cache and calls API
   */
  const handleUnassign = useCallback(async () => {
    // Close modal immediately
    setShowUnassignModal(false);

    // Optimistically update cache
    dispatch(
      notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
        draft.patient = null;
        draft.patient_id = null;
      }),
    );

    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIPostRequest({
        requestString: `/patient/unassignpatient`,
        accessToken,
        body: {
          note_id: noteId,
        },
      });

      if (response.ok) {
        enqueueSnackbar('Patient unassigned successfully', {
          variant: 'success',
        });
        // Track successful unassignment
        note.patientUnassigned(noteId, {
          patient_id: patient?.patient_id,
          encounter_id: encounterId,
        });
      } else {
        throw new Error('Failed to unassign patient');
      }
    } catch (error) {
      console.error('Error unassigning patient:', error);
      enqueueSnackbar('Failed to unassign patient', { variant: 'error' });

      // On error, invalidate cache to refetch correct data
      handleUnassignError();
    }
  }, [dispatch, noteId, getAccessToken, enqueueSnackbar, handleUnassignError]);

  /**
   * Navigate to patient page
   */
  const handlePatientClick = useCallback(() => {
    if (patient?.patient_id) {
      note.patientNameClicked(noteId, {
        patient_id: patient.patient_id,
        encounter_id: encounterId,
      });
      navigate(`/patient/${patient.patient_id}`);
    }
  }, [navigate, patient, noteId, encounterId, note]);

  /**
   * Handle unassign icon click
   */
  const handleUnassignClick = useCallback(() => {
    note.patientUnassignClicked(noteId, {
      patient_id: patient?.patient_id,
      encounter_id: encounterId,
    });
    setShowUnassignModal(true);
  }, [patient, noteId, encounterId, note]);

  /**
   * Handle medications indicator click
   */
  const handleMedicationsClick = useCallback(() => {
    if (!patient?.patient_id) return;
    setMedicationsModalOpen(true);
  }, [patient?.patient_id]);

  /**
   * Handle diagnoses indicator click
   */
  const handleDiagnosesClick = useCallback(() => {
    if (!patient?.patient_id) return;
    setDiagnosesModalOpen(true);
  }, [patient?.patient_id]);

  /**
   * Handle medications updated callback
   */
  const handleMedicationsUpdated = useCallback(() => {
    if (!patient?.patient_id) return;
    // Data will be refetched automatically by usePatientContext
  }, [patient?.patient_id]);

  /**
   * Handle diagnoses updated callback
   */
  const handleDiagnosesUpdated = useCallback(() => {
    if (!patient?.patient_id) return;
    // Data will be refetched automatically by usePatientContext
  }, [patient?.patient_id]);

  // Determine if encounter time is editable (data comes from encounter, not transcription)
  const isTimeEditable = editable && hasEncounterTimeData && Boolean(encounterId);

  // Determine if CPT codes are editable (only if we have encounter data)
  const isCptEditable = editable && Boolean(encounterId && encounterData);
  const hasCptCodes = hasLineItems(encounterData?.working_billing_claim);

  // Build metadata items array
  const metadataItems: Array<{
    icon: React.ReactNode;
    text: string;
    tooltip: string;
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;
    clickable?: boolean;
  }> = [];

  // Determine start time with fallback chain:
  // 1. encounterData.start_time (from separate useGetEncounterQuery hook)
  // 2. encounterData.appointment.start_time (if encounter doesn't have start_time directly)
  // 3. noteData.transcription_start_timestamp (fallback if encounter data unavailable)
  let startTimeEpoch: number | null = null;
  if (encounterData?.start_time) {
    // Encounter start_time is ISO 8601 string, convert to epoch seconds
    startTimeEpoch = new Date(encounterData.start_time).getTime() / 1000;
  } else if (encounterData?.appointment?.start_time) {
    // Appointment start_time is ISO 8601 string, convert to epoch seconds
    startTimeEpoch = new Date(encounterData.appointment.start_time).getTime() / 1000;
  } else if (noteData?.transcription_start_timestamp) {
    // Transcription timestamp is already in epoch seconds
    startTimeEpoch = noteData.transcription_start_timestamp;
  }

  // Calculate duration with fallback chain:
  // 1. Calculate from encounter start_time and end_time
  // 2. Use appointment duration (if encounter times not available)
  // 3. Fall back to transcription_length_seconds
  let durationSeconds: number | null = null;
  if (encounterData?.start_time && encounterData?.end_time) {
    // Calculate from encounter start/end times
    const start = new Date(encounterData.start_time).getTime();
    const end = new Date(encounterData.end_time).getTime();
    durationSeconds = Math.floor((end - start) / 1000);
  } else if (encounterData?.appointment?.duration) {
    // Appointment duration is in minutes, convert to seconds
    durationSeconds = encounterData.appointment.duration * 60;
  } else if (noteData?.transcription_length_seconds) {
    // Fall back to transcription length
    durationSeconds = noteData.transcription_length_seconds;
  }

  // Add encounter date/time as a single clickable item if available
  if (startTimeEpoch) {
    const startTimeFormatted = formatDate(startTimeEpoch, 'time').toLowerCase();
    // Use short month format (e.g., "Dec 11, 2025" instead of "December 11, 2025")
    const startTimeDate = new Date(startTimeEpoch * 1000);
    const dateText = format(startTimeDate, 'MMM d, yyyy');

    let timeText = '';
    if (durationSeconds) {
      // Format as time range with duration (e.g., "11:30 am – 12:15 pm, 45 min")
      const endTimeFormatted = formatDate(startTimeEpoch + durationSeconds, 'time').toLowerCase();

      // Format duration concisely
      const hours = Math.floor(durationSeconds / 3600);
      const minutes = Math.floor((durationSeconds % 3600) / 60);

      let durationText = '';
      if (hours > 0) {
        durationText = `${hours} hr${hours > 1 ? 's' : ''}`;
        if (minutes > 0) {
          durationText += ` ${minutes} min`;
        }
      } else if (minutes > 0) {
        durationText = `${minutes} min`;
      } else {
        durationText = `${Math.floor(durationSeconds)} sec`;
      }

      timeText = `${startTimeFormatted} – ${endTimeFormatted}, ${durationText}`;
    } else {
      timeText = startTimeFormatted;
    }

    metadataItems.push({
      icon: <CalendarToday />,
      text: `${dateText} • ${timeText}`,
      tooltip: isTimeEditable ? 'Click to edit encounter date and time' : 'Encounter Date and Time',
      onClick: isTimeEditable ? handleOpenTimeEditor : undefined,
      clickable: isTimeEditable,
    });
  }

  // Add payer/insurance chip between date and CPT
  const insuranceCoverages = patient?.insurance_coverages;
  const primaryCoverage = Array.isArray(insuranceCoverages) && insuranceCoverages.length > 0
    ? insuranceCoverages[0]
    : null;

  if (primaryCoverage?.payer_name) {
    const payerIcon = primaryCoverage.payer_logo_url ? (
      <Box
        component="img"
        src={primaryCoverage.payer_logo_url}
        alt={primaryCoverage.payer_name}
        sx={{ width: 20, height: 20, maxWidth: 20, maxHeight: 20, objectFit: 'contain', borderRadius: 0.5 }}
      />
    ) : (
      <HealthAndSafety />
    );

    metadataItems.push({
      icon: payerIcon,
      text: primaryCoverage.payer_name,
      tooltip: onPayerClick ? 'Click to view patient insurance' : `Payer: ${primaryCoverage.payer_name}`,
      onClick: onPayerClick ? () => {
        note.payerChipClicked(noteId, {
          encounter_id: encounterId,
          patient_id: patient?.patient_id,
          has_insurance: true,
          payer_name: primaryCoverage.payer_name ?? undefined,
        });
        onPayerClick();
      } : undefined,
      clickable: Boolean(onPayerClick),
    });
  } else if (patient?.patient_id && onPayerClick) {
    metadataItems.push({
      icon: <HealthAndSafety />,
      text: 'Insurance',
      tooltip: 'Click to add patient insurance',
      onClick: () => {
        note.payerChipClicked(noteId, {
          encounter_id: encounterId,
          patient_id: patient?.patient_id,
          has_insurance: false,
        });
        onPayerClick();
      },
      clickable: true,
    });
  }

  // Check if we have suggested codes to show as ghost
  const hasSuggestedCodes = Boolean(
    suggestedCptCodes &&
      suggestedCptCodes.length > 0 &&
      suggestedCptCodes.some(c => c && c.trim() !== ''),
  );
  const filteredSuggestedCodes = suggestedCptCodes?.filter(c => c && c.trim() !== '') || [];

  // Add CPT codes if encounter data is available
  // Show codes even when not editable (read-only), but only show prompt when editable
  if (hasCptCodes || (isCptEditable && !hasSuggestedCodes)) {
    if (hasCptCodes) {
      // Show existing CPT codes (clickable only when editable)
      const cptText = extractBillingCodes(encounterData?.working_billing_claim).join(', ');
      metadataItems.push({
        icon: <AttachMoney />,
        text: cptText,
        tooltip: isCptEditable ? 'Click to edit CPT codes' : 'CPT codes',
        onClick: isCptEditable ? handleOpenCptEditor : undefined,
        clickable: isCptEditable,
      });
    } else if (isCptEditable && !hasSuggestedCodes) {
      // No codes and no suggestions - show prompt to enter codes (only when editable)
      metadataItems.push({
        icon: <AttachMoney />,
        text: 'CPT',
        tooltip: 'Add CPT codes',
        onClick: handleOpenCptEditor,
        clickable: true,
      });
    }
    // Note: When hasSuggestedCodes is true and !hasCptCodes, we render ghost codes separately below
  }

  // Determine if ICD-10 codes are editable (only if we have encounter data)
  const isIcd10Editable = editable && Boolean(encounterId && encounterData);
  const hasIcd10Codes = hasDiagnosisCodes(encounterData?.working_billing_claim);

  // Add ICD-10 codes if encounter data is available
  // Show codes even when not editable (read-only), but only show prompt when editable
  if (hasIcd10Codes || isIcd10Editable) {
    if (hasIcd10Codes) {
      // Show primary code + count of additional codes
      const icd10Codes = extractDiagnosisCodes(encounterData?.working_billing_claim);
      const icd10Text = icd10Codes.length > 1
        ? `${icd10Codes[0]} +${icd10Codes.length - 1}`
        : icd10Codes[0];
      metadataItems.push({
        icon: <LocalHospital />,
        text: icd10Text,
        tooltip: isIcd10Editable
          ? `Click to edit ICD-10 codes: ${icd10Codes.join(', ')}`
          : `ICD-10: ${icd10Codes.join(', ')}`,
        onClick: isIcd10Editable ? handleOpenIcd10Editor : undefined,
        clickable: isIcd10Editable,
      });
    } else if (isIcd10Editable) {
      // No codes - show prompt to enter codes (only when editable)
      metadataItems.push({
        icon: <LocalHospital />,
        text: 'ICD-10',
        tooltip: 'Add diagnosis codes',
        onClick: handleOpenIcd10Editor,
        clickable: true,
      });
    }
  }

  return (
    <Box
      sx={{
        bgcolor: '#FCFCFC',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Container
        maxWidth="xxl"
        sx={{
          px: LAYOUT_SPACING.SECTION_HORIZONTAL_PADDING,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          py: 2,
          columnGap: 3,
          rowGap: 0.5,
        }}
      >
        {/* Patient Assignment or Display */}
        {!hasPatient && !hasPatientId ? (
          editable && (
            <AssignNoteToPatient
              noteId={noteId}
              encounterId={encounterId}
              onAssign={handleAssignPatient}
              onAssignError={handleAssignError}
            />
          )
        ) : !hasPatient && hasPatientId ? (
          <Tooltip title={`A ${subjectLanguage} is assigned to this note, but you don't have permission to view their information. Ask your practice administrator if you think you should have access.`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'default' }}>
              <UserIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '1.375rem',
                  fontWeight: 500,
                  color: theme.palette.text.disabled,
                  fontStyle: 'italic',
                }}
              >
                {capitalize(subjectLanguage)} assigned
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            onMouseEnter={() => editable && setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {/* Patient name, pronouns, eligibility icon, and context indicators - all on same line */}
            {editable && (
              <IconButton size="small" onClick={handleUnassignClick} sx={{ p: 0.5 }}>
                {isHovering ? (
                  <CloseIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                ) : (
                  <UserIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                )}
              </IconButton>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="body2"
                onClick={handlePatientClick}
                sx={{
                  fontSize: '1.375rem',
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  cursor: 'pointer',
                  textDecoration: isHovering ? 'underline' : 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatPatientName(
                  patient?.first_name,
                  patient?.last_name,
                  (patient as any)?.preferred_name,
                )}
                {/* Conditionally display pronouns if available */}
                {(patient as any)?.pronouns && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      ml: 0.5,
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    ({(patient as any).pronouns})
                  </Typography>
                )}
              </Typography>
              {hasEligibilityChecks ? (
                <EligibilityBadge
                  patientId={patient?.patient_id}
                  encounterId={encounterId}
                  source="note_metadata"
                  onAddInsurance={handleAddInsurance}
                />
              ) : (
                <EligibilityWarningIcon
                  patientId={patient?.patient_id}
                  source="note_metadata"
                  onAddInsurance={handleAddInsurance}
                />
              )}
            </Box>
            {/* Patient context indicators on same line */}
            {patient && (
              <PatientIndicators
                medicationsHover={medicationsHover}
                diagnosesHover={diagnosesHover}
                allergiesHover={allergiesHover}
                lastEncounterHover={lastEncounterHover}
                onMedicationsClick={handleMedicationsClick}
                onDiagnosesClick={handleDiagnosesClick}
              />
            )}
          </Box>
        )}

        {/* Metadata Items */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 2,
            rowGap: 1,
            '& > *:not(:last-child)::after': {
              content: '""',
              display: 'inline-block',
              width: '1px',
              height: 24,
              bgcolor: theme.palette.divider,
              ml: 2,
            },
          }}
        >
          {metadataItems.map((item, index) => (
            <MetadataItem
              key={index}
              icon={item.icon}
              text={item.text}
              tooltip={item.tooltip}
              onClick={item.onClick}
              clickable={item.clickable}
            />
          ))}
        </Box>

        {/* Ghost Suggested CPT Codes - shown when no codes exist but suggestions are available */}
        {isCptEditable && !hasCptCodes && hasSuggestedCodes && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ml: 2,
              pl: 2,
              borderLeft: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Tooltip
              title="Click to edit CPT codes. JotPsych suggests these codes based on your note content"
              arrow
            >
              <Box
                onClick={handleOpenCptEditor}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                  mx: -1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <AttachMoney
                  fontSize="small"
                  sx={{ color: theme.palette.text.disabled, flexShrink: 0 }}
                />
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {filteredSuggestedCodes.map((code, idx) => (
                    <Chip
                      key={idx}
                      label={code}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        opacity: 0.6,
                        borderStyle: 'dashed',
                        borderColor: theme.palette.text.disabled,
                        color: theme.palette.text.secondary,
                        pointerEvents: 'none', // Prevent chip clicks from interfering
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Tooltip>
            {onUseSuggestedCodes && (
              <Tooltip title="Accept suggested codes" arrow>
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation(); // Prevent opening the CPT picker
                    onUseSuggestedCodes();
                  }}
                  sx={{
                    p: 0.5,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                    },
                  }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {/* CPT Codes Editor Popover */}
        <Popover
          open={isCptEditorOpen}
          anchorEl={cptAnchorEl}
          onClose={handleCloseCptEditor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, minWidth: 300, maxWidth: 500 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Box>
                  <Typography variant="h6">CPT Codes</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Enter up to two CPT codes for this encounter (optional).
                  </Typography>
                </Box>
                <IconButton onClick={handleCloseCptEditor} size="small" sx={{ ml: 2 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <CPTCodePicker
                value={extractBillingCodes(encounterData?.working_billing_claim)}
                lineItems={encounterData?.working_billing_claim?.line_items ?? undefined}
                onChange={handleBillingCodesChange}
                showHeader={false}
                suggestedCodes={suggestedCptCodes}
                onUseSuggestedCodes={
                  onUseSuggestedCodes
                    ? () => {
                        onUseSuggestedCodes();
                        // Close the popover after applying suggested codes
                        setCptAnchorEl(null);
                      }
                    : undefined
                }
              />
            </Stack>
          </Box>
        </Popover>

        {/* ICD-10 Codes Editor Popover */}
        <Popover
          open={isIcd10EditorOpen}
          anchorEl={icd10AnchorEl}
          onClose={handleCloseIcd10Editor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, minWidth: 300, maxWidth: 500 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Box>
                  <Typography variant="h6">ICD-10 Diagnosis Codes</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Enter up to 4 diagnosis codes. The first code is the primary diagnosis.
                  </Typography>
                </Box>
                <IconButton onClick={handleCloseIcd10Editor} size="small" sx={{ ml: 2 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <ICD10CodePicker
                value={extractDiagnosisCodes(encounterData?.working_billing_claim)}
                onChange={handleDiagnosisCodesChange}
                showHeader={false}
              />
            </Stack>
          </Box>
        </Popover>

        {/* Unassignment Confirmation Modal */}
        <ConfirmDialog
          open={showUnassignModal}
          variant="warning"
          title="Unassign Patient"
          message={`Are you sure you want to unassign ${formatPatientName(
            patient?.first_name,
            patient?.last_name,
            (patient as any)?.preferred_name,
          )} from this note?`}
          onClose={() => setShowUnassignModal(false)}
          onConfirm={handleUnassign}
          confirmText="Unassign"
        />
      </Container>

      {/* Patient Context Popovers */}
      {patient && (
        <>
          {/* Medications Popover */}
          <PatientMedicationsPopover
            medications={medications}
            anchorEl={medicationsHover.anchorEl}
            open={medicationsHover.isHovered && !!medicationsHover.anchorEl}
            onClose={() => {
              medicationsHover.handlePopoverLeave();
            }}
            onMouseEnter={medicationsHover.handlePopoverEnter}
            onMouseLeave={medicationsHover.handlePopoverLeave}
            isLoading={loadingState.medications}
            onEdit={handleMedicationsClick}
          />

          {/* Diagnoses Popover */}
          <PatientDiagnosesPopover
            diagnoses={diagnoses}
            anchorEl={diagnosesHover.anchorEl}
            open={diagnosesHover.isHovered && !!diagnosesHover.anchorEl}
            onClose={() => {
              diagnosesHover.handlePopoverLeave();
            }}
            onMouseEnter={diagnosesHover.handlePopoverEnter}
            onMouseLeave={diagnosesHover.handlePopoverLeave}
            isLoading={loadingState.diagnoses}
            onEdit={handleDiagnosesClick}
          />

          {/* Allergies Popover */}
          <PatientAllergiesPopover
            allergies={allergies}
            anchorEl={allergiesHover.anchorEl}
            open={allergiesHover.isHovered && !!allergiesHover.anchorEl}
            onClose={() => {
              allergiesHover.handlePopoverLeave();
            }}
            onMouseEnter={allergiesHover.handlePopoverEnter}
            onMouseLeave={allergiesHover.handlePopoverLeave}
            isLoading={loadingState.allergies}
          />

          {/* Last Encounter Popover */}
          <PatientLastEncounterPopover
            data={lastEncounter}
            anchorEl={lastEncounterHover.anchorEl}
            open={lastEncounterHover.isHovered && !!lastEncounterHover.anchorEl}
            onClose={() => {
              lastEncounterHover.handlePopoverLeave();
            }}
            onMouseEnter={lastEncounterHover.handlePopoverEnter}
            onMouseLeave={lastEncounterHover.handlePopoverLeave}
            isLoading={loadingState.lastEncounter}
            onViewNote={onViewLastEncounterNote}
          />

          {/* Medications Edit Modal */}
          {patient.patient_id && medicationsModuleInfo && (
            <PatientMedicationsEditModal
              open={medicationsModalOpen}
              onClose={() => setMedicationsModalOpen(false)}
              patientId={patient.patient_id}
              medications={medications}
              moduleInfo={medicationsModuleInfo}
              onMedicationsUpdated={handleMedicationsUpdated}
            />
          )}

          {/* Diagnoses Edit Modal */}
          {patient.patient_id && diagnosesModuleInfo && (
            <PatientDiagnosesEditModal
              open={diagnosesModalOpen}
              onClose={() => setDiagnosesModalOpen(false)}
              patientId={patient.patient_id}
              diagnoses={diagnoses}
              moduleInfo={diagnosesModuleInfo}
              onDiagnosesUpdated={handleDiagnosesUpdated}
            />
          )}
        </>
      )}
    </Box>
  );
};
